import { QuestionCircleOutlined } from '@ant-design/icons';
import {
  Form,
  Input,
  Modal,
  PageHeader,
  Popconfirm,
  Space,
  Switch,
  Tooltip,
  Typography,
} from 'antd';
import { RevxTable } from 'components';
import { RevxButton } from 'components/RevxButton';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxSpin } from 'components/RevxSpin';
import { OrgColorsDTO } from 'domains/orgDomain';
import { useOrg } from 'hooks/useOrg';
import pantoneCoatedColors from 'pantoner/json/pantone-coated.json';
import pantoneColorOfTheYearColors from 'pantoner/json/pantone-color-of-the-year.json';
import pantoneMetallicColors from 'pantoner/json/pantone-metallic.json';
import pantonePastelsNeonsColors from 'pantoner/json/pantone-pastels-neons.json';
import pantoneSkinColors from 'pantoner/json/pantone-skin.json';
import pantoneUncoatedColors from 'pantoner/json/pantone-uncoated.json';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import orgService from 'services/orgService';

type PmsColor = {
  id: number;
  name: string;
  colorHex: string;
};

type DielinePmsConfig = {
  dielineDetectionEnabled: boolean;
  pmsDetectionEnabled: boolean;
  dielineColorHex: string;
  pmsColors: PmsColor[];
};

type PmsColorForm = {
  name: string;
};

type PantonerColor = {
  pantone: string;
  hex: string;
};

const DEFAULT_DIELINE_COLOR = '#FE00FE';
const PANTONE_COLORS = [
  ...(pantoneCoatedColors as PantonerColor[]),
  ...(pantoneUncoatedColors as PantonerColor[]),
  ...(pantoneMetallicColors as PantonerColor[]),
  ...(pantonePastelsNeonsColors as PantonerColor[]),
  ...(pantoneSkinColors as PantonerColor[]),
  ...(pantoneColorOfTheYearColors as PantonerColor[]),
].reduce<Record<string, string>>((colorMap, item) => {
  colorMap[item.pantone.toLowerCase()] = item.hex;
  return colorMap;
}, {});

const DEFAULT_CONFIG: DielinePmsConfig = {
  dielineDetectionEnabled: true,
  pmsDetectionEnabled: false,
  dielineColorHex: DEFAULT_DIELINE_COLOR,
  pmsColors: [],
};

const normalizeHexColor = (value: string) => value.trim().toUpperCase();

const isHexColor = (value?: string) =>
  Boolean(value?.match(/^#[0-9A-Fa-f]{6}$/));

const normalizePmsNameKey = (value?: string) => {
  const cleanedValue = (value || '')
    .trim()
    .toLowerCase()
    .replace(/^pantone\s+/, '')
    .replace(/^pms\s+/, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  if (cleanedValue.match(/-[cu]$/)) {
    return cleanedValue;
  }

  const compactSuffixMatch = cleanedValue.match(/^(.+)([cu])$/);
  const pmsNameKey = compactSuffixMatch
    ? `${compactSuffixMatch[1]}-${compactSuffixMatch[2]}`
    : cleanedValue;

  return PANTONE_COLORS[pmsNameKey] ? pmsNameKey : `${pmsNameKey}-c`;
};

const formatPmsName = (value: string) => {
  const pmsNameKey = normalizePmsNameKey(value);
  const suffixMatch = pmsNameKey.match(/^(.+)-([cu])$/);

  return suffixMatch
    ? `PMS ${suffixMatch[1]
        .replace(/-/g, ' ')
        .toUpperCase()} ${suffixMatch[2].toUpperCase()}`
    : `PMS ${pmsNameKey.replace(/-/g, ' ').toUpperCase()}`;
};

const resolvePmsColorHex = (value?: string) =>
  PANTONE_COLORS[normalizePmsNameKey(value)]?.toUpperCase();

const pmsNameRules = (message: string) => [
  { required: true, message },
  {
    validator: (_rule: unknown, value: string) =>
      resolvePmsColorHex(value) ? Promise.resolve() : Promise.reject(message),
  },
];

const PmsNameHelp = ({ title }: { title: string }) => (
  <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{title}</span>}>
    <QuestionCircleOutlined
      style={{ color: '#1890ff', cursor: 'help', fontSize: 14 }}
    />
  </Tooltip>
);

const toConfig = (orgColors?: OrgColorsDTO): DielinePmsConfig => ({
  dielineDetectionEnabled:
    orgColors?.dieLineColorConfig?.dielineDetectionEnabled ?? true,
  pmsDetectionEnabled: Boolean(orgColors?.pmsColorConfig?.pmsDetectionEnabled),
  dielineColorHex:
    orgColors?.dieLineColorConfig?.dielineColorHex || DEFAULT_DIELINE_COLOR,
  pmsColors: (orgColors?.pmsColorConfig?.pmsColors || []).map(
    (item, index) => ({
      id: index + 1,
      name: item.name,
      colorHex: item.colorHex || resolvePmsColorHex(item.name),
    })
  ),
});

const ColorSwatch = ({
  color,
  disabled,
}: {
  color: string;
  disabled?: boolean;
}) => (
  <span
    style={{
      display: 'inline-block',
      width: 24,
      height: 24,
      borderRadius: 4,
      border: '1px solid #d9d9d9',
      backgroundColor: isHexColor(color) ? color : '#fff',
      opacity: disabled ? 0.45 : 1,
      verticalAlign: 'middle',
    }}
  />
);

const DielinePmsColorConfig = () => {
  const { t } = useTranslation();
  const org = useOrg();
  const [pmsColorForm] = Form.useForm<PmsColorForm>();
  const [editPmsColorForm] = Form.useForm<PmsColorForm>();
  const [config, setConfig] = useState<DielinePmsConfig>(DEFAULT_CONFIG);
  const [editingPmsColor, setEditingPmsColor] = useState<PmsColor>();
  const [nextPmsColorId, setNextPmsColorId] = useState(1);
  const [editPmsColorPreviewHex, setEditPmsColorPreviewHex] =
    useState<string>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const orgId = org?.id || 0;
  const isDielineColorDefault =
    normalizeHexColor(config.dielineColorHex) === DEFAULT_DIELINE_COLOR;

  useEffect(() => {
    if (!orgId) return;
    let mounted = true;
    setLoading(true);
    orgService
      .getOrgColors(orgId)
      .then((orgColors) => {
        if (!mounted) return;
        const updatedConfig = toConfig(orgColors);
        setConfig(updatedConfig);
        setNextPmsColorId(
          updatedConfig.pmsColors.reduce(
            (maxId, item) => Math.max(maxId, item.id),
            0
          ) + 1
        );
        setEditingPmsColor(undefined);
        pmsColorForm.resetFields();
        setHasUnsavedChanges(false);
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, [orgId, pmsColorForm]);

  const updateConfig = (updates: Partial<DielinePmsConfig>) => {
    if (!orgId) return;
    setConfig((prev) => ({
      ...prev,
      ...updates,
    }));
    setHasUnsavedChanges(true);
  };

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!hasUnsavedChanges) return;
      event.preventDefault();
      event.returnValue = t('common.leaveConfirmQuestion');
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [hasUnsavedChanges, t]);

  const onSave = async () => {
    if (!orgId) return;
    setSaving(true);
    try {
      await orgService.saveOrgColors({
        orgId,
        dieLineColorConfig: {
          dielineDetectionEnabled: config.dielineDetectionEnabled,
          dielineColorHex: config.dielineColorHex,
        },
        pmsColorConfig: {
          pmsDetectionEnabled: config.pmsDetectionEnabled,
          pmsColors: config.pmsColors.map((item) => ({
            name: item.name,
            colorHex: item.colorHex,
          })),
        },
      });
      setHasUnsavedChanges(false);
    } finally {
      setSaving(false);
    }
  };

  const onDielineColorChange = (value: string) => {
    updateConfig({ dielineColorHex: normalizeHexColor(value) });
  };

  const onResetDielineColor = () => {
    updateConfig({ dielineColorHex: DEFAULT_DIELINE_COLOR });
  };

  const onPmsColorSubmit = async () => {
    let values: PmsColorForm;
    try {
      values = await pmsColorForm.validateFields();
    } catch (validationError) {
      return;
    }
    const pmsColor: PmsColor = {
      id: nextPmsColorId,
      name: formatPmsName(values.name),
      colorHex: resolvePmsColorHex(values.name),
    };
    updateConfig({
      pmsColors: [...config.pmsColors, pmsColor],
    });
    setNextPmsColorId((value) => value + 1);
    pmsColorForm.resetFields();
  };

  const onEditPmsColor = (record: PmsColor) => {
    setEditingPmsColor(record);
    editPmsColorForm.setFieldsValue({
      name: record.name,
    });
    setEditPmsColorPreviewHex(record.colorHex);
  };

  const onUpdatePmsColor = async () => {
    if (!editingPmsColor) return;
    let values: PmsColorForm;
    try {
      values = await editPmsColorForm.validateFields();
    } catch (validationError) {
      return;
    }
    const pmsColor: PmsColor = {
      id: editingPmsColor.id,
      name: formatPmsName(values.name),
      colorHex: resolvePmsColorHex(values.name),
    };
    updateConfig({
      pmsColors: config.pmsColors.map((item) =>
        item.id === editingPmsColor.id ? pmsColor : item
      ),
    });
    setEditingPmsColor(undefined);
    editPmsColorForm.resetFields();
    setEditPmsColorPreviewHex(undefined);
  };

  const onCancelEditPmsColor = () => {
    setEditingPmsColor(undefined);
    editPmsColorForm.resetFields();
    setEditPmsColorPreviewHex(undefined);
  };

  const onRemovePmsColor = (id: number) => {
    updateConfig({
      pmsColors: config.pmsColors.filter((item) => item.id !== id),
    });
    if (editingPmsColor?.id === id) {
      onCancelEditPmsColor();
    }
  };

  const pmsColumns = [
    {
      title: t('platform.resources.dielinePmsColor.pmsColorName'),
      dataIndex: 'name',
    },
    {
      title: t('platform.resources.dielinePmsColor.color'),
      dataIndex: 'colorHex',
      width: 100,
      render: (value: string) => <ColorSwatch color={value} />,
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 180,
      render: (_value: unknown, record: PmsColor) => (
        <RevxFlexLayout>
          <RevxButton
            type="link"
            size="small"
            disabled={!config.pmsDetectionEnabled}
            onClick={() => onEditPmsColor(record)}
          >
            {t('common.edit')}
          </RevxButton>
          <Popconfirm
            title={t('common.confirmQuestion')}
            icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
            onConfirm={() => onRemovePmsColor(record.id)}
            okText={t('common.ok')}
            cancelText={t('common.cancel')}
            disabled={!config.pmsDetectionEnabled}
          >
            <RevxButton
              type="link"
              size="small"
              disabled={!config.pmsDetectionEnabled}
            >
              {t('common.remove')}
            </RevxButton>
          </Popconfirm>
        </RevxFlexLayout>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title={t('platform.resources.tabs.dielinePmsColor')}
        subTitle={org ? `${org.id} - ${org.name}` : ''}
        className="platform-page-header"
        ghost={false}
        extra={[
          <Space key="save" size="middle">
            {hasUnsavedChanges && (
              <Typography.Text strong type="warning">
                {t('platform.resources.dielinePmsColor.unsavedChanges')}
              </Typography.Text>
            )}
            <RevxButton
              type="primary"
              brandGreen={!hasUnsavedChanges}
              brandOrange={hasUnsavedChanges}
              disabled={!orgId}
              loading={saving}
              onClick={onSave}
              size={hasUnsavedChanges ? 'large' : undefined}
              style={
                hasUnsavedChanges
                  ? {
                      minWidth: 112,
                      fontWeight: 700,
                      boxShadow: '0 0 0 3px rgba(250, 140, 22, 0.18)',
                    }
                  : undefined
              }
            >
              {t('common.save')}
            </RevxButton>
          </Space>,
        ]}
      />

      <RevxSpin spinning={loading || saving}>
        <div style={{ padding: 24, maxWidth: 920 }}>
          <section
            style={{
              marginBottom: 32,
              padding: 20,
              border: '1px solid #f0f0f0',
              backgroundColor: config.dielineDetectionEnabled
                ? '#fff'
                : '#fafafa',
            }}
          >
            <RevxFlexLayout
              justify="space-between"
              style={{ alignItems: 'center', marginBottom: 16 }}
            >
              <Typography.Title level={5} style={{ marginBottom: 0 }}>
                {t('platform.resources.dielinePmsColor.dielineColor')}
              </Typography.Title>
              <Space>
                <Switch
                  checked={config.dielineDetectionEnabled}
                  disabled={!orgId}
                  onChange={(checked) =>
                    updateConfig({ dielineDetectionEnabled: checked })
                  }
                />
                <span>
                  {t('platform.resources.dielinePmsColor.dielineDetection')}
                </span>
              </Space>
            </RevxFlexLayout>
            <div
              style={{
                opacity: config.dielineDetectionEnabled ? 1 : 0.55,
                pointerEvents: config.dielineDetectionEnabled ? 'auto' : 'none',
              }}
            >
              <RevxFlexLayout style={{ gap: 12 }}>
                <Input
                  style={{ width: 140 }}
                  value={config.dielineColorHex}
                  disabled={!config.dielineDetectionEnabled}
                  onChange={(event) => onDielineColorChange(event.target.value)}
                />
                <input
                  type="color"
                  value={
                    isHexColor(config.dielineColorHex)
                      ? config.dielineColorHex
                      : DEFAULT_DIELINE_COLOR
                  }
                  disabled={!config.dielineDetectionEnabled}
                  onInput={(event) =>
                    onDielineColorChange(event.currentTarget.value)
                  }
                  onChange={(event) => onDielineColorChange(event.target.value)}
                  style={{ width: 44, height: 32 }}
                />
                <ColorSwatch
                  color={config.dielineColorHex}
                  disabled={!config.dielineDetectionEnabled}
                />
                {!isDielineColorDefault && (
                  <RevxButton
                    type="link"
                    size="small"
                    disabled={!config.dielineDetectionEnabled}
                    onClick={onResetDielineColor}
                    style={{ fontSize: 12, padding: 0 }}
                  >
                    {t('platform.resources.dielinePmsColor.resetDefault')}
                  </RevxButton>
                )}
              </RevxFlexLayout>
            </div>
          </section>

          <section
            style={{
              padding: 20,
              border: '1px solid #f0f0f0',
              backgroundColor: config.pmsDetectionEnabled ? '#fff' : '#fafafa',
            }}
          >
            <RevxFlexLayout
              justify="space-between"
              style={{ alignItems: 'center', marginBottom: 16 }}
            >
              <Typography.Title level={5} style={{ marginBottom: 0 }}>
                {t('platform.resources.dielinePmsColor.pmsColors')}
              </Typography.Title>
              <Space>
                <Switch
                  checked={config.pmsDetectionEnabled}
                  disabled={!orgId}
                  onChange={(checked) =>
                    updateConfig({ pmsDetectionEnabled: checked })
                  }
                />
                <span>
                  {t('platform.resources.dielinePmsColor.pmsDetection')}
                </span>
              </Space>
            </RevxFlexLayout>
            <div
              style={{
                opacity: config.pmsDetectionEnabled ? 1 : 0.55,
                pointerEvents: config.pmsDetectionEnabled ? 'auto' : 'none',
              }}
            >
              <Form<PmsColorForm>
                form={pmsColorForm}
                layout="inline"
                style={{ marginBottom: 16 }}
              >
                <Form.Item
                  name="name"
                  rules={pmsNameRules(
                    t('platform.resources.dielinePmsColor.pmsNameValidation')
                  )}
                >
                  <Input
                    disabled={!config.pmsDetectionEnabled}
                    placeholder={t(
                      'platform.resources.dielinePmsColor.pmsColorName'
                    )}
                  />
                </Form.Item>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    height: 32,
                    marginRight: 16,
                  }}
                >
                  <PmsNameHelp
                    title={t(
                      'platform.resources.dielinePmsColor.pmsNameExample'
                    )}
                  />
                </span>
                <RevxButton
                  type="primary"
                  brandGreen
                  disabled={!config.pmsDetectionEnabled}
                  onClick={onPmsColorSubmit}
                >
                  {t('common.create')}
                </RevxButton>
              </Form>
              <RevxTable<PmsColor>
                size="small"
                rowKey="id"
                columns={pmsColumns}
                dataSource={config.pmsColors}
                pagination={false}
              />
            </div>
          </section>
        </div>
      </RevxSpin>
      <Modal
        destroyOnClose
        visible={Boolean(editingPmsColor)}
        title={t('common.edit')}
        okText={t('common.update')}
        cancelText={t('common.cancel')}
        onOk={onUpdatePmsColor}
        onCancel={onCancelEditPmsColor}
      >
        <Form<PmsColorForm> form={editPmsColorForm} layout="vertical">
          <Form.Item
            label={
              <Space size={4}>
                {t('platform.resources.dielinePmsColor.pmsColorName')}
                <PmsNameHelp
                  title={t('platform.resources.dielinePmsColor.pmsNameExample')}
                />
              </Space>
            }
            name="name"
            rules={pmsNameRules(
              t('platform.resources.dielinePmsColor.pmsNameValidation')
            )}
          >
            <Input
              onChange={(event) =>
                setEditPmsColorPreviewHex(
                  resolvePmsColorHex(event.target.value)
                )
              }
            />
          </Form.Item>
          <Space>
            <ColorSwatch color={editPmsColorPreviewHex || ''} />
            {editPmsColorPreviewHex && (
              <Typography.Text>{editPmsColorPreviewHex}</Typography.Text>
            )}
          </Space>
        </Form>
      </Modal>
    </div>
  );
};

export default DielinePmsColorConfig;
